import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { apiAdminDeleteUser, apiAdminImpersonate, apiAdminListUsers, type AdminUserRow } from "../api";
import { useAuth } from "../SessionContext";

function formatJoined(ts: number) {
  try {
    return new Date(ts * 1000).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

export function AdminPage() {
  const { isAdmin, impersonator, refresh } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { users: rows } = await apiAdminListUsers();
      setUsers(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function openUserBudget(targetUserId: string) {
    setBusyId(targetUserId);
    setError(null);
    try {
      await apiAdminImpersonate(targetUserId);
      await refresh();
      navigate("/", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open user");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteUser(user: AdminUserRow) {
    const ok = window.confirm(
      `Delete ${user.email}? This permanently removes their accounts, categories, transactions, and transfers.`,
    );
    if (!ok) return;
    setBusyId(user.id);
    setError(null);
    try {
      await apiAdminDeleteUser(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete user");
    } finally {
      setBusyId(null);
    }
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="stack">
      <div className="row-between">
        <div>
          <h1 className="mt-0">Admin</h1>
          <p className="muted mb-0">Registered users. Select an email to view and edit that user&apos;s budget.</p>
        </div>
      </div>

      {impersonator ? (
        <p className="small muted mb-0">
          You are currently viewing another user&apos;s data. Use the banner above to return here or to your own
          account.
        </p>
      ) : null}

      {error ? <p className="error">{error}</p> : null}

      {loading ? (
        <p className="muted">Loading users…</p>
      ) : (
        <div className="card">
          <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Joined</th>
                <th scope="col" className="right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      disabled={busyId !== null}
                      onClick={() => void openUserBudget(u.id)}
                    >
                      {u.email}
                    </button>
                    {busyId === u.id ? <span className="muted small"> …</span> : null}
                  </td>
                  <td className="muted">{formatJoined(u.createdAt)}</td>
                  <td className="right">
                    <button
                      type="button"
                      className="btn-ghost btn-sm btn-danger"
                      disabled={busyId !== null}
                      onClick={() => void deleteUser(u)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
